import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import { fetchIntegrations } from "../api/queries";
import { PROVIDERS } from "../pages/integrations/providers";

/**
 * The store's integrations, matched to the provider catalog.
 *
 * `connected` pairs each connected provider with its saved record (the first
 * one, if the API ever returns two of a type — it has no uniqueness rule);
 * `available` is every provider not connected yet, in catalog order.
 * `integrations` is every record the API returned, duplicates included; the
 * page resolves ?edit=<id> against it so any record can be opened.
 */
export const useIntegrationsData = () => {
  const { selectedStore } = useStore();
  const storeId = selectedStore?.id;

  const { data, isLoading } = useQuery({
    queryKey: ["integrations", storeId],
    queryFn: () => fetchIntegrations(storeId),
    enabled: !!storeId,
  });

  const integrations = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const { connected, available } = useMemo(() => {
    const connected = [];
    const available = [];
    PROVIDERS.forEach((provider) => {
      const integration = integrations.find((i) => i.integration_type === provider.key);
      if (integration) connected.push({ provider, integration });
      else available.push(provider);
    });
    return { connected, available };
  }, [integrations]);

  return { storeId, integrations, connected, available, isLoading };
};
